import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminViews, isForbidden, schedule as scheduleApi, timesheets } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import { Icon, Origin, Pill } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { ADMIN_VIEWS_READ, usePermissions } from '../permissions.js';
import { formatClock, formatDay } from '../time/timeLogic.js';
import { chipState } from './scheduleBoard.js';
import { CoveragePanel } from './CoveragePanel.js';
import { AttributionPicker } from './AttributionPicker.js';
import { CancellationRecord } from './CancellationRecord.js';
import { REASON_LABEL } from './cancellationLogic.js';

/** "1h 05m", for a non-negative minute count. */
function hoursAndMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}m` : `${minutes}m`;
}

/**
 * Plan versus actual, in one phrase, with all four states kept apart.
 *
 *   null      — the session has no computed minutes, so nobody has measured
 *               this yet. An open session is the ordinary case. NOT "0m".
 *   zero      — measured, and exactly to plan. A real result.
 *   positive  — over plan.
 *   negative  — under plan.
 *
 * The scheduled figure travels with every one of them: "18m over plan" alone
 * is unreadable without knowing whether the plan was 30 minutes or eight hours.
 */
export function varianceLabel(comparison: {
  scheduledMinutes: number;
  varianceMinutes: number | null;
}): string {
  const planned = `${hoursAndMinutes(comparison.scheduledMinutes)} planned`;
  if (comparison.varianceMinutes === null) {
    return `${planned} · not yet measured`;
  }
  if (comparison.varianceMinutes === 0) {
    return `${planned} · exactly to plan`;
  }
  const magnitude = hoursAndMinutes(Math.abs(comparison.varianceMinutes));
  return `${planned} · ${magnitude} ${comparison.varianceMinutes > 0 ? 'over' : 'under'} plan`;
}

/**
 * THE SHIFT CONTEXT DRAWER (Phase D — owner directive: PLAN → ACTUAL →
 * RECONCILED, "context follows the person/shift/service event", and "do not
 * fabricate joined data that cannot actually be read").
 *
 * One shift, three layers, each layer drawn ONLY from a read that exists and
 * only for a principal whose keys open it:
 *
 *   PLAN       — the shift row itself (already in hand from the roster read).
 *   ACTUAL     — the linked session, via `GET /api/admin-views/sessions/:id`
 *                (`admin-views:read`, the key the route charges). No session
 *                and a started shift is the coverage gap, said as itself.
 *   RECONCILED — the worker's timesheet week covering the shift
 *                (`GET /timesheets/workers/:id/week`, `timesheet:read` with a
 *                server-side reach check). Attempted only for a
 *                `timesheet:approve` holder; a 403 renders as a refusal.
 *
 * Per-shift MILEAGE is deliberately absent: no `tripsForShift` read exists
 * (engineering request M4), and the drawer does not fake the join — the
 * Mileage door below is the honest path. Documentation status per shift is
 * absent for the same reason.
 *
 * Sections a principal's keys do not open are ABSENT, not ghosts, except
 * where a held key's read then refuses — that renders as the refusal it is.
 */
export function ShiftDrawer({
  shift,
  workerName,
  workerUserId,
  programName,
  siteName,
  now,
  onClose,
  onEdit,
  onCancelShift,
  canWrite,
  canManage,
  canceling,
  cancelPending,
  cancelErrorMessage,
  onConfirmCancel,
  onKeepShift,
}: {
  shift: SCH.ShiftWithCoverage;
  workerName: string;
  /**
   * The worker's `app_user.id`, resolved by the parent from the directory it
   * already reads. `null` when the directory has not answered or the reader may
   * not read it, which the cancellation record renders as UNDETERMINED rather
   * than asserting who answered.
   */
  workerUserId: string | null;
  programName: string | null;
  siteName: string | null;
  now: Date;
  onClose: () => void;
  /** Opens the existing edit form for this shift (the parent owns the draft). */
  onEdit: (shift: SCH.ShiftWithCoverage) => void;
  /** Starts the existing cancel confirmation for this shift. */
  onCancelShift: (shift: SCH.ShiftWithCoverage) => void;
  canWrite: boolean;
  canManage: boolean;
  /** The parent's cancel-confirmation state, so the confirm lives where the
   *  button is and a failed cancel stays visibly uncancelled (the rule
   *  schedule-cancel-failure-is-not-success.test.tsx holds for the list). */
  canceling: boolean;
  cancelPending: boolean;
  cancelErrorMessage: string | null;
  /**
   * THE FULL DOOR. Takes the attribution, and optionally the reason, because
   * the point of this door is that a cancellation is a record and not a status
   * change. The parent owns the mutation; the draft lives here, beside the
   * control that fills it in.
   */
  onConfirmCancel: (input: SCH.CancelWithAttributionInput) => void;
  onKeepShift: () => void;
}) {
  const navigate = useNav();
  const { has } = usePermissions();
  const canReadSession = has(ADMIN_VIEWS_READ);
  const canReadTimesheet = has('timesheet:approve');
  const canReadSchedule = has('schedule:read');

  const [attributedTo, setAttributedTo] = useState<SCH.Attribution | ''>('');
  /**
   * The reason, IF THE PERSON CANCELLING ALREADY KNOWS IT.
   *
   * Optional at cancellation time, and the emptiness is load-bearing rather than
   * lazy: whoever calls a shift off often does not know why, and a required
   * field here is what produces the fictional reasons that make the whole record
   * worthless. Leaving it empty is also what CAUSES the worker to be asked — the
   * server puts the single ask only when a worker-attributed cancellation
   * arrives with no reason — so a scheduler who fills this in on the worker's
   * behalf is deciding that no question needs to go out.
   */
  const [reasonCategory, setReasonCategory] = useState<string>('');
  const [reasonNote, setReasonNote] = useState('');

  const state = chipState(shift, now);

  const session = useQuery({
    queryKey: ['admin-views', 'session', shift.session?.id],
    queryFn: () => adminViews.session(shift.session!.id),
    enabled: canReadSession && shift.session !== null,
  });

  const week = useQuery({
    queryKey: ['timesheets', 'week', shift.workerId, shift.startsAt],
    queryFn: () => timesheets.workerWeek(shift.workerId, shift.startsAt),
    enabled: canReadTimesheet,
  });

  return (
    <aside className="drawer" aria-label={`Shift context for ${workerName}`}>
      <div className="drawer-head">
        <div>
          <h2 className="rn-card-title">{workerName}</h2>
          <p className="rn-muted" style={{ margin: 0 }}>
            {formatDay(shift.startsAt)} · {formatClock(shift.startsAt)} –{' '}
            {formatClock(shift.endsAt)}
          </p>
        </div>
        <button type="button" className="drawer-close" aria-label="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>

      {/* PLAN */}
      <section className="drawer-section">
        <h3 className="drawer-label">Plan</h3>
        <div className="drawer-rows">
          <div className="drawer-row">
            <span className="rn-muted">Status</span>
            <Pill variant={stateTone(state)} dot>
              {stateLabel(state, shift.status)}
            </Pill>
          </div>
          {/* PH-UX-002. A shift carries only `source` — no installation id,
              no freshness, no vendor-absence — so an imported one can say
              only THAT it was imported; `Origin` renders nothing otherwise. */}
          {shift.source === 'imported' ? (
            <div className="drawer-row">
              <span className="rn-muted">Origin</span>
              <Origin source={shift.source} />
            </div>
          ) : null}
          {programName ? (
            <div className="drawer-row">
              <span className="rn-muted">Program</span>
              <span>{programName}</span>
            </div>
          ) : null}
          {siteName ? (
            <div className="drawer-row">
              <span className="rn-muted">Site</span>
              <span>{siteName}</span>
            </div>
          ) : null}
          {shift.notes ? (
            <div className="drawer-row">
              <span className="rn-muted">Notes</span>
              <span>{shift.notes}</span>
            </div>
          ) : null}
        </div>
        {canWrite && shift.status !== 'cancelled' ? (
          <div className="drawer-actions">
            <button type="button" className="rn-btn-quiet" onClick={() => onEdit(shift)}>
              Edit shift
            </button>
            {canManage ? (
              canceling ? null : (
                <button type="button" className="rn-btn-quiet" onClick={() => onCancelShift(shift)}>
                  Cancel shift
                </button>
              )
            ) : null}
          </div>
        ) : null}

        {/* ------------------------------------------------------------------
            CALLING IT OFF — the record, not the status change.

            The confirmation is a FORM rather than a pair of buttons because
            three facts are being recorded and only one of them is "cancelled":
            whose side it came from, why (if the person here already knows), and
            in whose words. `attributedTo` has no default and the confirm is
            disabled without it, so the record cannot acquire an attribution
            nobody chose.
           ------------------------------------------------------------------ */}
        {canWrite && canManage && shift.status !== 'cancelled' && canceling ? (
          <div className="drawer-rows" style={{ marginTop: 'var(--space-8)' }}>
            <AttributionPicker
              value={attributedTo}
              onChange={setAttributedTo}
              id={`drawer-cancel-attribution-${shift.id}`}
              explain
            />
            <label className="rn-muted" htmlFor={`drawer-cancel-reason-${shift.id}`}>
              Reason, if you already know it
            </label>
            <select
              id={`drawer-cancel-reason-${shift.id}`}
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
            >
              {/* NOT "none". Leaving this empty is what causes the ask to go
                  out, so the option says what it does. */}
              <option value="">Don’t know yet — ask {workerName.split(' ')[0]}</option>
              {scheduleApi.CANCELLATION_REASON_CATEGORIES.map((key: string) => (
                <option key={key} value={key}>
                  {REASON_LABEL[key] ?? key}
                </option>
              ))}
            </select>
            {reasonCategory === '' ? null : (
              <textarea
                aria-label="Reason note"
                rows={2}
                value={reasonNote}
                placeholder="What happened, in as few words as it takes"
                onChange={(e) => setReasonNote(e.target.value)}
              />
            )}
            <div className="drawer-actions">
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={attributedTo === '' || cancelPending}
                onClick={() => {
                  if (attributedTo === '') return;
                  onConfirmCancel({
                    attributedTo,
                    reasonCategory:
                      reasonCategory === ''
                        ? null
                        : (reasonCategory as SCH.CancellationReasonCategory),
                    reasonNote: reasonNote.trim() === '' ? null : reasonNote.trim(),
                  });
                }}
              >
                {cancelPending
                  ? 'Cancelling…'
                  : cancelErrorMessage
                    ? 'Try again'
                    : 'Confirm cancel'}
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setAttributedTo('');
                  setReasonCategory('');
                  setReasonNote('');
                  onKeepShift();
                }}
              >
                Keep it
              </button>
            </div>
            {attributedTo === '' ? (
              <p className="rn-muted" style={{ margin: 0 }}>
                Choose who this is attributable to before confirming. A cancellation with no
                attributed side cannot answer, later, what happened here.
              </p>
            ) : null}
          </div>
        ) : null}
        {canceling && cancelErrorMessage ? (
          <p role="status" className="rn-muted" style={{ color: 'var(--color-urgent-deep)' }}>
            {cancelErrorMessage} This shift is still on the schedule.
          </p>
        ) : null}
      </section>

      {/*
        THE CANCELLATION RECORD — what a "cancelled" pill could never say.

        Rendered only for a cancelled shift, and only for a reader who holds
        `schedule:read`; the route additionally asks whether this reader reaches
        THIS worker's roster, and a refusal is drawn as a refusal rather than as
        an empty section.
      */}
      {shift.status === 'cancelled' && canReadSchedule ? (
        <CancellationRecord shiftId={shift.id} workerUserId={workerUserId} />
      ) : null}

      {/*
        COVERAGE — who could take this shift, and whether the client is
        authorized to receive it.

        Placed immediately after PLAN because it is a question about the plan:
        a scheduler reading a gap wants the answer here rather than on another
        screen. Absent entirely without `schedule:manage` (the panel decides),
        which is the drawer's own rule about keys a reader does not hold.

        Not rendered for a cancelled shift: there is nothing left to staff, and
        recommending cover for a shift that no longer exists is an offer that
        cannot be honoured.
      */}
      {shift.status === 'cancelled' ? null : <CoveragePanel shiftId={shift.id} now={now} />}

      {/* ACTUAL */}
      {canReadSession ? (
        <section className="drawer-section">
          <h3 className="drawer-label">Actual</h3>
          {shift.session === null ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              {state === 'gap'
                ? 'This shift started and nothing was recorded against it. That may be a worker who forgot, cover by someone else, or a plan that changed — Phibian records the gap; a person decides what it means.'
                : state === 'upcoming'
                  ? 'Not started yet — nothing to record.'
                  : 'No session is linked to this shift.'}
            </p>
          ) : session.isLoading ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              Reading the session…
            </p>
          ) : session.isError ? (
            isForbidden(session.error) ? (
              <p className="rn-muted" style={{ margin: 0 }}>
                The session behind this shift is not shown to you.
              </p>
            ) : (
              <p className="rn-muted" style={{ margin: 0 }}>
                The session couldn’t be read just now.{' '}
                <button type="button" className="rn-link" onClick={() => void session.refetch()}>
                  Try again
                </button>
              </p>
            )
          ) : session.data ? (
            <div className="drawer-rows">
              <div className="drawer-row">
                <span className="rn-muted">Clocked in</span>
                <span>{formatClock(session.data.clockedInAt)}</span>
              </div>
              <div className="drawer-row">
                <span className="rn-muted">Clocked out</span>
                <span>
                  {session.data.clockedOutAt ? (
                    formatClock(session.data.clockedOutAt)
                  ) : (
                    <Pill variant="sky" dot>
                      still open
                    </Pill>
                  )}
                </span>
              </div>
              {session.data.computedMinutes !== null ? (
                <div className="drawer-row">
                  <span className="rn-muted">Worked</span>
                  <span>
                    {Math.floor(session.data.computedMinutes / 60)}h{' '}
                    {session.data.computedMinutes % 60}m
                  </span>
                </div>
              ) : null}
              {/*
                PLAN vs ACTUAL — the drawer's whole reason for existing, and it
                was on the wire and dropped on the floor.

                `GET /api/admin-views/sessions/:id` has computed
                `scheduledComparison` per request since it shipped:
                `scheduledMinutes`, `actualMinutes`, `varianceMinutes` and
                `onTime`. This component already fetched that payload and
                rendered `computedMinutes` — the ACTUAL alone, with nothing to
                compare it to — so a supervisor reading "Worked 7h 45m" could
                not tell whether that was the plan, an hour over it, or an hour
                short, and the drawer's own header names PLAN → ACTUAL →
                RECONCILED as the thing it is for.

                FOUR STATES, NOT TWO. `varianceMinutes` is null when the session
                has no computed minutes yet — an open session — and that is
                "not yet", not zero. A real zero is a shift worked exactly to
                plan and says so. Rendering the null as "0m" would report a
                measurement nobody has taken as a perfect result, which is the
                collapse this codebase keeps finding.
              */}
              {session.data.scheduledComparison ? (
                <div className="drawer-row">
                  <span className="rn-muted">Against plan</span>
                  <span>{varianceLabel(session.data.scheduledComparison)}</span>
                </div>
              ) : null}
              {session.data.scheduledComparison?.onTime !== null &&
              session.data.scheduledComparison?.onTime !== undefined ? (
                <div className="drawer-row">
                  <span className="rn-muted">Start</span>
                  {session.data.scheduledComparison.onTime ? (
                    <Pill variant="good" dot>
                      on time
                    </Pill>
                  ) : (
                    <Pill variant="look" dot>
                      late
                    </Pill>
                  )}
                </div>
              ) : null}
              {session.data.breadcrumbCoverageStatus ? (
                <div className="drawer-row">
                  <span className="rn-muted">Location</span>
                  <span>{session.data.breadcrumbCoverageStatus.replace(/_/g, ' ')}</span>
                </div>
              ) : null}
              {session.data.exceptions.length > 0 ? (
                <div className="drawer-row">
                  <span className="rn-muted">Exceptions</span>
                  <Pill variant="look" dot>
                    {String(session.data.exceptions.length)}
                  </Pill>
                </div>
              ) : null}
              <button
                type="button"
                className="rn-link"
                style={{ textAlign: 'left', padding: 0 }}
                onClick={() => navigate(`/sessions/${shift.session!.id}`)}
              >
                Open the full session →
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* RECONCILED */}
      {canReadTimesheet ? (
        <section className="drawer-section">
          <h3 className="drawer-label">Reconciled</h3>
          {week.isLoading ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              Reading the timesheet week…
            </p>
          ) : week.isError ? (
            isForbidden(week.error) ? (
              <p className="rn-muted" style={{ margin: 0 }}>
                This worker’s timesheet is not shown to you.
              </p>
            ) : (
              <p className="rn-muted" style={{ margin: 0 }}>
                The timesheet couldn’t be read just now.{' '}
                <button type="button" className="rn-link" onClick={() => void week.refetch()}>
                  Try again
                </button>
              </p>
            )
          ) : week.data ? (
            <div className="drawer-rows">
              <div className="drawer-row">
                <span className="rn-muted">Timesheet</span>
                <Pill variant={timesheetTone(week.data.period.status)} dot>
                  {week.data.period.status.replace(/_/g, ' ')}
                </Pill>
              </div>
              <div className="drawer-row">
                <span className="rn-muted">Week total</span>
                <span>
                  {/* CORE-AUD-216: no time clock is not a 0h 0m week. */}
                  {week.data.hasTimeClock === false
                    ? 'Not measured — no time clock connected'
                    : `${Math.floor(week.data.aggregate.totalMinutes / 60)}h ${week.data.aggregate.totalMinutes % 60}m`}
                </span>
              </div>
              {week.data.exceptions.length > 0 ? (
                <div className="drawer-row">
                  <span className="rn-muted">Week exceptions</span>
                  <Pill variant="look" dot>
                    {String(week.data.exceptions.length)}
                  </Pill>
                </div>
              ) : null}
              <button
                type="button"
                className="rn-link"
                style={{ textAlign: 'left', padding: 0 }}
                onClick={() =>
                  navigate(
                    `/time/timesheets/${shift.workerId}?weekOf=${encodeURIComponent(shift.startsAt)}`,
                  )
                }
              >
                Open the timesheet →
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Doors — context follows the person. Mileage stays a DOOR, not a
          fabricated join (M4 in the engineering handoff). */}
      <section className="drawer-section">
        <h3 className="drawer-label">More about {workerName.split(' ')[0]}</h3>
        <div className="drawer-doors">
          <button
            type="button"
            className="rn-link"
            onClick={() => navigate(`/people/${shift.workerId}`)}
          >
            Profile →
          </button>
          {has('mileage:review') ? (
            <button type="button" className="rn-link" onClick={() => navigate('/mileage')}>
              Mileage →
            </button>
          ) : null}
        </div>
      </section>
    </aside>
  );
}

function stateTone(
  state: ReturnType<typeof chipState>,
): 'good' | 'look' | 'unsure' | 'plain' | 'sky' {
  switch (state) {
    case 'live':
      return 'sky';
    case 'covered':
      return 'good';
    case 'gap':
      return 'look';
    case 'cancelled':
      return 'plain';
    case 'upcoming':
      return 'unsure';
  }
}

function stateLabel(state: ReturnType<typeof chipState>, status: string): string {
  switch (state) {
    case 'live':
      return 'on the clock';
    case 'covered':
      return 'worked';
    case 'gap':
      return 'started, no session';
    case 'cancelled':
      return 'cancelled';
    case 'upcoming':
      return status === 'confirmed' ? 'confirmed' : 'scheduled';
  }
}

function timesheetTone(status: string): 'good' | 'look' | 'unsure' | 'plain' {
  switch (status) {
    case 'approved':
      return 'good';
    case 'returned':
      return 'look';
    case 'submitted':
      return 'unsure';
    default:
      return 'plain';
  }
}
